import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { LoaderIcon } from '../icons';

const DataMigrationPrompt = ({ isOpen, onMigrate, onSkip, onClose, favorites = [], tags = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleMigrate = async () => {
    setLoading(true);
    setError('');
    
    try {
      await onMigrate();
      onClose();
    } catch (error) {
      console.error('Migration error:', error);
      setError('Failed to migrate your data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onSkip();
    onClose();
  };

  const totalItems = favorites.length + tags.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
              <path d="M12 2v20"></path>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            Keep Your Data?
          </DialogTitle>
          <DialogDescription className="text-center">
            You have {totalItems > 0 ? `${totalItems} saved items` : 'some data'} from browsing as a guest.
            Would you like to keep this data in your new account?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {totalItems > 0 && (
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <h4 className="font-medium text-sm">Your data includes:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                {favorites.length > 0 && (
                  <li className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500">
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7z"></path>
                    </svg>
                    {favorites.length} favorite {favorites.length === 1 ? 'property' : 'properties'}
                  </li>
                )}
                {tags.length > 0 && (
                  <li className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                    </svg>
                    {tags.length} collection{tags.length === 1 ? '' : 's'}
                  </li>
                )}
              </ul>
            </div>
          )}
          
          <div className="space-y-3">
            <Button
              onClick={handleMigrate}
              disabled={loading}
              className="w-full h-12 text-sm font-medium"
            >
              {loading ? (
                <>
                  <LoaderIcon className="mr-2 w-5 h-5" />
                  Migrating Your Data...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Yes, Keep My Data
                </>
              )}
            </Button>
            
            <Button
              onClick={handleSkip}
              disabled={loading}
              variant="outline"
              className="w-full h-12 text-sm font-medium"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              No, Start Fresh
            </Button>
          </div>
          
          <div className="text-xs text-muted-foreground text-center">
            <p>
              If you skip this step, your guest data will be lost and cannot be recovered.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DataMigrationPrompt;